import Raven from 'raven-js';
import { EXTENDED_SIGNUP_V1 } from 'common/onboardingConstants';
import typeCheck from 'common/util/typeCheck';

import { anonymousUser } from 'client/constants';
import safeLocalStorage from 'client/lib/safeLocalStorage';
import sendDataToMobileApp, {
  SR_MOBILE_APP_ACTION_TYPE_CURRENT_USER,
} from 'client/helpers/sendDataToMobileApp';

import onboardingBackToStep from 'client/actions/Onboarding/backToStep';
import loadRenterProfileAction from 'client/actions/Household/loadRenterProfile';

import { SR_API_ACTION_SIGNUP } from 'api/actionTypes';

import { SR_ERROR_USER_ALREADY_EXISTS } from 'common/errorCodes';

export default async function signup({ state, apiClient }, params) {
  const analyticsSessionId = state.get(['analytics', 'analyticsSessionId']);
  const experimentActiveVariants = state.get(['analytics', 'experiments', 'activeVariants']);
  const utmSource = state.get(['analytics', 'utmValues', 'source']);
  const isMobileApp = state.get(['mobileApp', 'isMobileApp']);

  // Get the phone number onboarding step to redirect the user to the phone number step, in case the signup API call fails because the user already exists.
  typeCheck('onboardingSteps::NonEmptyArray', params.onboardingSteps);
  typeCheck('signupReason::NonEmptyString', params.signupReason);

  const viewedListingIdsStr = safeLocalStorage.getItem('viewedListingIds');
  const viewedListingIds = viewedListingIdsStr ? JSON.parse(viewedListingIdsStr) : [];

  let newUser;
  const emailOnboardingStep = params.onboardingSteps.indexOf('email') + 1;
  typeCheck('emailOnboardingStep::PositiveNumber', emailOnboardingStep);
  await apiClient.postWithState({
    action: SR_API_ACTION_SIGNUP,
    state: state,
    payload: {
      user: {
        name: params.name,
        password: params.password,
        phone: params.phoneNumber,
        email: params.email,
        signupAnalyticsSessionId: analyticsSessionId,
        signupUtmSource: utmSource,
        leaseTerm: params.leaseTerm,
        leaseSituation: params.leaseSituation,
        moveInDate: params.moveInDate,
        searchFilters: {
          propertyTypes: params.propertyTypes,
          minRentPrice: params.minRentPrice,
          maxRentPrice: params.maxRentPrice,
          area: params.area,
          bedrooms:
            params.signupReason === EXTENDED_SIGNUP_V1 ? params.bedroomsMulti : params.bedrooms,
          bathrooms: params.bathrooms || 0,
          petCount: params.petCount,
          pets: params.signupReason === EXTENDED_SIGNUP_V1 ? ['dog'] : params.pets,
          moveInDate: params.moveInDate,
          availableHomesNearMoveInDate: false,
          section8: false,
          marketId: params.marketId,
        },
        experimentActiveVariants: experimentActiveVariants,
        startTouring: params.startTouring || null,
        laundryPreference: params.laundryPreference || null,
      },
      viewedListingIds: viewedListingIds.length > 0 ? viewedListingIds : null,
      apartmentLocatorUserId: params.apartmentLocatorUserId,
      listingId: params.listingId,
    },
    onSuccess({ user }) {
      newUser = {
        ...anonymousUser,
        ...user,
      };
      // Set the new user as the current user in the state.
      state.set('currentUser', newUser);
      state.set(['search', 'filters'], newUser.searchFilters);
      state.set(['tours'], []);
      state.set(['bookmarksByListingId'], {});
      state.set(['checkAvailabilityByListingId'], {});
      state.set(['householdApplications'], []);

      safeLocalStorage.removeItem('viewedListingIds');

      return loadRenterProfileAction({ state, apiClient }, { userId: user.id });
    },
    onError(error) {
      if (error.constraint === 'users_email_unique') {
        onboardingBackToStep({ state, apiClient }, params.onboardingSteps.indexOf('email') + 1);
      }
      if (error.name === SR_ERROR_USER_ALREADY_EXISTS) {
        onboardingBackToStep(
          { state, apiClient },
          params.onboardingSteps.indexOf('phoneNumber') + 1,
        );
      }
    },
  });

  if (newUser) {
    Raven.setUserContext(newUser);

    if (isMobileApp) {
      sendDataToMobileApp({
        actionType: SR_MOBILE_APP_ACTION_TYPE_CURRENT_USER,
        currentUser: newUser,
      });
    }
  }

  return newUser;
}
